import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Database, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Input, Skeleton, Switch, Textarea } from '@/components/ui/primitives';
import { SalutationMapCard, type NameOrder, type SalutationMeaning } from '@/components/salutation-map';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime, formatRelative } from '@/lib/utils';

interface PowerbirdConfig {
  configured: boolean;
  host: string | null;
  port: number;
  instanceName: string | null;
  database: string | null;
  username: string | null;
  hasPassword: boolean;
  encrypt: boolean;
  trustServerCertificate: boolean;
  query: string;
  intervalMinutes: number;
  active: boolean;
  createCustomers: boolean;
  status: 'UNTESTED' | 'OK' | 'ERROR';
  lastSyncAt: string | null;
  lastError: string | null;
  lastResult: Record<string, number> | null;
  salutationMap: Record<string, SalutationMeaning>;
  privateNameOrder: NameOrder;
  salutationMeanings: { key: string; label: string }[];
  linkedCustomers: number;
  openChanges: number;
  columns: { name: string; label: string }[];
  template: string;
}

type TestResult = { ok: boolean; message: string; columns?: string[]; missing?: string[]; sample?: { number: string; name: string; city: string | null }[] };

const KEY = ['integrations', 'powerbird'];
const onError = (e: unknown) => toast.error(errorMessage(e));

export function PowerbirdPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: KEY, queryFn: () => api<PowerbirdConfig>('/integrations/powerbird') });
  const [form, setForm] = useState({
    host: '',
    port: 1433,
    instanceName: '',
    database: '',
    username: '',
    password: '',
    encrypt: true,
    trustServerCertificate: false,
    query: '',
    intervalMinutes: 60,
    active: true,
    createCustomers: true,
    salutationMap: {} as Record<string, SalutationMeaning>,
    privateNameOrder: 'AUTO' as NameOrder,
  });
  const [test, setTest] = useState<TestResult | null>(null);
  useEffect(() => {
    if (data)
      setForm({
        host: data.host ?? '',
        port: data.port,
        instanceName: data.instanceName ?? '',
        database: data.database ?? '',
        username: data.username ?? '',
        password: '',
        encrypt: data.encrypt,
        trustServerCertificate: data.trustServerCertificate,
        query: data.query,
        intervalMinutes: data.intervalMinutes,
        active: data.active,
        createCustomers: data.createCustomers,
        salutationMap: data.salutationMap,
        privateNameOrder: data.privateNameOrder,
      });
  }, [data]);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: () => api<PowerbirdConfig>('/integrations/powerbird', { method: 'PUT', body: { ...form, password: form.password || undefined, instanceName: form.instanceName || null } }),
    onSuccess: (r) => {
      qc.setQueryData(KEY, r);
      toast.success('Gespeichert');
    },
    onError,
  });
  const runTest = useMutation({ mutationFn: () => api<TestResult>('/integrations/powerbird/test', { method: 'POST' }), onSuccess: (r) => (setTest(r), qc.invalidateQueries({ queryKey: KEY })), onError });
  const sync = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>('/integrations/powerbird/sync', { method: 'POST' }),
    onSuccess: (r) => {
      (r.ok ? toast.success : toast.error)(r.message, { duration: 10_000 });
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError,
  });

  if (!data) return <Skeleton className="h-96" />;
  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Powerbird"
        description="Kunden aus der Powerbird-Datenbank (SQL Server) nur lesend übernehmen. Änderungen und neue Kunden im Portal meldet das Portal der Buchhaltung zum Nachtragen in Powerbird."
      />
      <div className="grid gap-4">
        {data.configured && (
          <Card>
            <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-5 text-sm">
              <span className="flex items-center gap-2">
                <Database className="size-4 text-muted-foreground" />
                {data.status === 'OK' && <Badge variant="success">Verbunden</Badge>}
                {data.status === 'ERROR' && <Badge variant="destructive">Fehler</Badge>}
                {data.status === 'UNTESTED' && <Badge variant="outline">Nicht geprüft</Badge>}
                {!data.active && <Badge variant="outline">inaktiv</Badge>}
              </span>
              <span>{data.linkedCustomers} Kunden aus Powerbird</span>
              <span>Letzter Abgleich: {data.lastSyncAt ? formatRelative(data.lastSyncAt) : 'noch keiner'}</span>
              {data.openChanges > 0 && (
                <Link to="/billing?tab=customers" className="text-primary hover:underline">
                  {data.openChanges} offene Hinweise für die Buchhaltung
                </Link>
              )}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => runTest.mutate()} loading={runTest.isPending}>
                  <CheckCircle2 /> Verbindung testen
                </Button>
                <Button size="sm" variant="outline" onClick={() => sync.mutate()} loading={sync.isPending}>
                  <RefreshCw /> Jetzt abgleichen
                </Button>
              </div>
              {data.lastError && <Alert variant="destructive" className="w-full">{data.lastError}</Alert>}
              {data.lastResult && !data.lastError && (
                <p className="w-full text-xs text-muted-foreground">
                  Letztes Ergebnis ({formatDateTime(data.lastSyncAt)}): {data.lastResult.rows} Zeilen, {data.lastResult.created} neu, {data.lastResult.linked} verknüpft, {data.lastResult.updated} aktualisiert
                  {data.lastResult.skippedFields ? `, ${data.lastResult.skippedFields} Felder wegen offener Hinweise nicht überschrieben` : ''}.
                </p>
              )}
            </CardContent>
          </Card>
        )}
        {/* Fehler stehen bereits in der Statuskarte */}
        {test?.ok && (
          <Alert variant="success">
            <p>{test.message}</p>
            {test.columns && <p className="mt-1 text-xs">Spalten: {test.columns.join(', ')}</p>}
            {test.sample && test.sample.length > 0 && <p className="mt-1 text-xs">Beispiele: {test.sample.map((s) => `${s.number} ${s.name}${s.city ? ` (${s.city})` : ''}`).join(' · ')}</p>}
          </Alert>
        )}

        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SQL Server</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Server (Name oder IP)" htmlFor="pb-host">
                <Input id="pb-host" required value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="z. B. sql01.firma.local" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Port" htmlFor="pb-port" hint="leer lassen bei Instanz">
                  <Input id="pb-port" type="number" min={1} max={65535} value={form.port} onChange={(e) => set('port', Number(e.target.value) || 1433)} />
                </Field>
                <Field label="Instanz (optional)" htmlFor="pb-inst">
                  <Input id="pb-inst" value={form.instanceName} onChange={(e) => set('instanceName', e.target.value)} placeholder="z. B. POWERBIRD" />
                </Field>
              </div>
              <Field label="Datenbank" htmlFor="pb-db">
                <Input id="pb-db" required value={form.database} onChange={(e) => set('database', e.target.value)} />
              </Field>
              <Field label="Benutzer (nur Leserechte!)" htmlFor="pb-user" hint="Eigener SQL-Benutzer mit db_datareader bzw. SELECT auf die Kundentabelle">
                <Input id="pb-user" required value={form.username} onChange={(e) => set('username', e.target.value)} autoComplete="off" />
              </Field>
              <Field label="Passwort" htmlFor="pb-pass" hint={data.hasPassword ? 'Gespeichert. Nur zum Ändern ausfüllen.' : undefined}>
                <Input id="pb-pass" type="password" autoComplete="new-password" required={!data.hasPassword} value={form.password} onChange={(e) => set('password', e.target.value)} placeholder={data.hasPassword ? '•••••••• (unverändert)' : ''} />
              </Field>
              <div className="grid gap-2 text-sm">
                <label className="flex items-center gap-2">
                  <Switch checked={form.encrypt} onCheckedChange={(v) => set('encrypt', v)} /> Verbindung verschlüsseln
                </label>
                <label className="flex items-center gap-2">
                  <Switch checked={form.trustServerCertificate} onCheckedChange={(v) => set('trustServerCertificate', v)} /> Serverzertifikat vertrauen (selbstsigniert)
                </label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Abfrage der Kunden</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <p className="text-sm text-muted-foreground">
                Nur eine einzelne <code>SELECT</code>-Abfrage; schreibende Befehle werden abgewiesen und die Abfrage läuft in einer Transaktion, die immer zurückgerollt wird. Die Spalten werden über ihre
                Namen zugeordnet (<code>… AS nummer</code>). „Vorlage einsetzen“ füllt die Abfrage für die Powerbird-Kundentabelle <code>ELKUN</code>; mit „Verbindung testen“ prüfen.
              </p>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {data.columns.map((c) => (
                  <Badge key={c.name} variant="outline" title={c.label}>
                    {c.name}
                    <span className="ml-1 font-normal text-muted-foreground">– {c.label}</span>
                  </Badge>
                ))}
              </div>
              <Textarea rows={12} className="font-mono text-xs" value={form.query} onChange={(e) => set('query', e.target.value)} aria-label="SQL-Abfrage" spellCheck={false} />
              <div>
                <Button type="button" variant="ghost" size="sm" onClick={() => set('query', data.template)}>
                  Vorlage einsetzen
                </Button>
              </div>
            </CardContent>
          </Card>

          <SalutationMapCard
            map={form.salutationMap}
            onMap={(m) => set('salutationMap', m)}
            nameOrder={form.privateNameOrder}
            onNameOrder={(o) => set('privateNameOrder', o)}
            meanings={data.salutationMeanings}
            configured={data.configured}
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Abgleich</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm sm:grid-cols-3">
              <Field label="Alle … Minuten" htmlFor="pb-int">
                <Input id="pb-int" type="number" min={15} max={1440} value={form.intervalMinutes} onChange={(e) => set('intervalMinutes', Number(e.target.value) || 60)} />
              </Field>
              <label className="flex items-center gap-2 self-end pb-2">
                <Switch checked={form.createCustomers} onCheckedChange={(v) => set('createCustomers', v)} /> Kunden nur aus Powerbird im Portal anlegen
              </label>
              <label className="flex items-center gap-2 self-end pb-2">
                <Switch checked={form.active} onCheckedChange={(v) => set('active', v)} /> Aktiv (Abgleich und Hinweise)
              </label>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button type="submit" loading={save.isPending}>
              Speichern
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
